/** UI / API bin ids: only these two are valid for new rankings. */
export const BIN_LABELS = {
  liked: 'Liked it!',
  disliked: "Didn't like it",
};

/** Composite list order: all liked (by within-bin rank), then all disliked. */
export const BIN_SORT_ORDER = { liked: 0, disliked: 1 };

function normalizeStoredBin(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.trim();
  if (v === 'liked') return 'liked';
  if (v === 'disliked') return 'disliked';
  if (v === 'okay' || v === 'mid') return 'disliked';
  return null;
}

export function legacyNotesToBin(notes) {
  if (notes === 'loved') return 'liked';
  if (notes === 'okay' || notes === 'dislike') return 'disliked';
  return null;
}

export function effectiveRankingBin(post) {
  if (!post) return null;
  const fromStored = normalizeStoredBin(post.rankingBin);
  if (fromStored) return fromStored;
  return legacyNotesToBin(post.notes);
}

export function isRankedPost(post) {
  return typeof post?.rankPosition === 'number';
}

function listMovieKey(movie) {
  const title = String(movie?.title || '').trim().toLowerCase();
  const year = movie?.year == null ? '' : String(movie.year);
  return `${title}__${year}`;
}

/** Latest watched-movies row for this title/year (ranked or not). */
export function findViewerPostForMovie(posts, movie) {
  if (!movie?.title || !Array.isArray(posts)) return null;
  const key = listMovieKey(movie);
  const matches = posts.filter((p) => listMovieKey(p.movie) === key);
  if (matches.length === 0) return null;
  matches.sort((a, b) => {
    const ac = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bc = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bc - ac;
  });
  return matches[0];
}

export function hasMovieInWatchlist(list, movie) {
  if (!movie?.title) return false;
  const key = listMovieKey(movie);
  return (list || []).some((item) => listMovieKey(item.movie) === key);
}

export function findWatchlistEntryForMovie(list, movie) {
  if (!movie?.title) return null;
  const key = listMovieKey(movie);
  return (list || []).find((entry) => listMovieKey(entry.movie) === key) || null;
}

export function findExistingRankedPost(posts, movie) {
  if (!movie?.title || !Array.isArray(posts)) return null;
  const key = listMovieKey(movie);
  const matches = posts.filter((p) => {
    if (!isRankedPost(p)) return false;
    return listMovieKey(p.movie) === key;
  });
  if (matches.length === 0) return null;
  matches.sort((a, b) => {
    const ac = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bc = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bc - ac;
  });
  return matches[0];
}

function round1(x) {
  return Math.round(x * 10) / 10;
}

/**
 * Scores: composite order = liked tier (best→worst) then disliked tier.
 * Liked tier scores ∈ [6, 10]; disliked tier ∈ [1, 5].
 */
export function buildScoreMap(posts) {
  const map = new Map();
  if (!Array.isArray(posts)) return map;

  const ranked = posts.filter(
    (p) => isRankedPost(p) && effectiveRankingBin(p)
  );
  const liked = ranked
    .filter((p) => effectiveRankingBin(p) === 'liked')
    .sort((a, b) => (a.rankPosition ?? 0) - (b.rankPosition ?? 0));
  const disliked = ranked
    .filter((p) => effectiveRankingBin(p) === 'disliked')
    .sort((a, b) => (a.rankPosition ?? 0) - (b.rankPosition ?? 0));

  const L = liked.length;
  const D = disliked.length;

  let compositeRank = 1;

  if (L === 1) {
    map.set(liked[0].id, {
      score: 10,
      kind: 'liked',
      compositeRank: compositeRank++,
    });
  } else if (L > 1) {
    liked.forEach((p, idx) => {
      const i = idx + 1;
      const s = 10 - (i - 1) * (4 / (L - 1));
      map.set(p.id, {
        score: round1(s),
        kind: 'liked',
        compositeRank: compositeRank++,
      });
    });
  }

  if (D === 1) {
    map.set(disliked[0].id, {
      score: 5,
      kind: 'disliked',
      compositeRank: compositeRank++,
    });
  } else if (D > 1) {
    disliked.forEach((p, idx) => {
      const j = idx + 1;
      const s = 5 - (j - 1) * (4 / (D - 1));
      map.set(p.id, {
        score: round1(s),
        kind: 'disliked',
        compositeRank: compositeRank++,
      });
    });
  }

  return map;
}

export function compositeSortCompare(a, b) {
  const abin = effectiveRankingBin(a);
  const bbin = effectiveRankingBin(b);
  const ar = typeof a.rankPosition === 'number';
  const br = typeof b.rankPosition === 'number';
  if (ar && !br) return -1;
  if (!ar && br) return 1;
  if (!ar && !br) return 0;

  const ao =
    abin != null && BIN_SORT_ORDER[abin] !== undefined
      ? BIN_SORT_ORDER[abin]
      : 99;
  const bo =
    bbin != null && BIN_SORT_ORDER[bbin] !== undefined
      ? BIN_SORT_ORDER[bbin]
      : 99;
  if (ao !== bo) return ao - bo;
  const ap = a.rankPosition ?? 0;
  const bp = b.rankPosition ?? 0;
  if (ap !== bp) return ap - bp;
  const ad = a.watchedAt ? Date.parse(a.watchedAt) : 0;
  const bd = b.watchedAt ? Date.parse(b.watchedAt) : 0;
  return bd - ad;
}
